import { fromJS, Map, List } from 'immutable'
// eslint-disable-next-line import/extensions, import/no-unresolved
import { parse } from 'csv-parse/browser/esm'
import _get from 'lodash/get'
import _gt from 'lodash/gt'
import _map from 'lodash/map'
import _split from 'lodash/split'
import reduce from 'lodash/reduce'
import _parseInt from 'lodash/parseInt'
import _isNumber from 'lodash/isNumber'
import _isInteger from 'lodash/isInteger'
import { CONTENT_REFERENCE_TYPES, CONTENT_REFERENCE_TYPE_CONTENT_TO_TYPES as REFERENCE_MAP } from '../content-references/types'
import { CONTENT_BULK_FIELDS, CONTENT_BULK_INTEGER_FIELDS } from '.'

function validateRelationship (value) {
  if (List.isList(value)) {
    return value.every((v) => validateRelationship(v))
  }
  return value === '' || (_isInteger(value) && _gt(value, 0))
}

function validate (row, column) {
  const value = row.get(column)

  if (CONTENT_REFERENCE_TYPES.includes(column)) {
    const valid = validateRelationship(value)
    return [valid, !valid && `"${column}" must be a number or a list of numbers`]
  }

  switch (column) {
    case 'contentType': {
      const valid = !!value
      return [valid, !valid && 'the "contentType" field is required']
    }
    case 'language': {
      const valid = /^(en|es|fr|de)$/.test(value)
      return [valid, !valid && 'the "language" field is required']
    }
    case 'contentId': {
      const valid = _isNumber(value)
      return [valid, !valid && 'the "contentId" field is required']
    }
    case 'publishedAtDate': {
      const isCorrectFormat = /^\d{4}-\d{2}-\d{2}$/.test(value)
      return [isCorrectFormat, !isCorrectFormat && 'the "publishedAtDate" field must be of format yyyy-mm-dd']
    }
    default:
      return [true]
  }
}

function parseValue (value, column) {
  if (CONTENT_REFERENCE_TYPES.includes(column)) {
    const values = _split(value, /,\s?/g)
    return _map(values, (v) => {
      const val = _parseInt(v)
      return Number.isFinite(val) ? val : v
    })
  }
  if (CONTENT_BULK_INTEGER_FIELDS.includes(column)) {
    return _parseInt(value)
  }
  return value
}

export async function parseCSV (file) {
  return new Promise((resolve) => {
    const type = _get(file, 'type')

    if (type !== 'text/csv') {
      resolve({
        error: 'File must be of type text/csv',
        content: List(),
        status: List(),
        source: List(),
      })
      return
    }

    const fileReader = new FileReader()
    fileReader.onload = (e) => {
      const contents = _get(e, 'target.result')
      parse(contents, {
        columns_duplicates_to_array: true,
        columns: true,
      }, (err, data) => {
        // convert to Immutable.
        // 1. remove any capitalization or other potential oddities
        // 2. coming in from the csv file that would cause a column mapping issue.
        const rows = List(_map(data, (row) => reduce(row, (map, value, key) => {
          const columnLower = key.toLowerCase()
          const column = CONTENT_REFERENCE_TYPES.includes(columnLower)
            ? columnLower : (key.charAt(0).toLowerCase() + key.slice(1))
          const rawValue = fromJS(parseValue(value, column))
          return map.set(column, rawValue)
        }, Map())))
        // create a status array for each row
        // representing a state (like success, error, etc.) and any additional data
        // that isn't directly related to content / content tables.
        // TODO: add YUP validation for fields instead of this basic-ness.
        const status = rows.map((row) => {
          const messages = row.reduce((list, value, column) => {
            const [valid, message] = validate(row, column)
            return valid ? list : list.push(message)
          }, List())
          return Map({
            success: messages.size < 1,
            messages,
          })
        })
        // create content rows with
        // a. strings converted to numbers when necessary
        // b. a reference array built from the raw csv columns
        const content = rows.map((row) => row.reduce((map, value, key) => {
          if (CONTENT_BULK_FIELDS.includes(key)) {
            return map.set(key, value)
          }
          if (CONTENT_REFERENCE_TYPES.includes(key)) {
            const refs = map.get('references', List())
            const values = List.isList(value) ? value : List(value)
            const nextRefs = refs.concat(values.map((v) => (Map({
              contentType: REFERENCE_MAP.get(key),
              contentId: v,
              type: key,
            }))))
            return map.set('references', nextRefs)
          }
          return map
        }, Map()))
        resolve(Map({
          error: err ? `Error parsing file: ${err}` : null,
          source: rows,
          content,
          status,
        }))
      })
    }
    fileReader.readAsText(file)
  })
}
